import React from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import PrintIcon from "@mui/icons-material/Print";
import { HistoryRow } from "../types";
import { balanceColor, COLOR, fmt, getTodayInTZ } from "../utils/format";
import { MONTH_STATUS_LABEL, RECONCILIATION_LABEL } from "../utils/reportSections";

interface Props {
  year: number;
  rows: HistoryRow[];
  loading: boolean;
  selected: { year: number; month: number } | null;
  busyKey: string | null; // "2026-9" while that row's PDF/print is being prepared
  onYearChange: (year: number) => void;
  onOpenMonth: (year: number, month: number) => void;
  onPdf: (year: number, month: number) => void;
  onPrint: (year: number, month: number) => void;
}

const REC_COLOR = { not_entered: "default", reconciled: "success", needs_review: "error" } as const;
const STATUS_COLOR = { open: "default", reconciled: "success", closed: "primary" } as const;
const HEADERS = ["Month", "Carry Forward", "Income", "Expenses", "Closing Balance", "Actual Balance", "Difference", "Reconciliation", "Status", "PDF", "Print"];
const RIGHT = new Set([1, 2, 3, 4, 5, 6]);

/**
 * Month-by-month history; every figure comes from the backend. Click a row
 * to load that month. PDF / Print fetch that row's month — never the one
 * currently on screen.
 */
const MonthlyHistory: React.FC<Props> = ({ year, rows, loading, selected, busyKey, onYearChange, onOpenMonth, onPdf, onPrint }) => {
  const thisYear = getTodayInTZ().year;
  const years: number[] = [];
  for (let y = thisYear; y >= thisYear - 6; y--) years.push(y);

  return (
    <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
      <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 2, borderBottom: "1px solid #eee" }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Monthly History
        </Typography>
        <TextField select size="small" value={year} onChange={(e) => onYearChange(Number(e.target.value))} sx={{ ml: "auto", width: 100 }}>
          {years.map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: COLOR.headerBg }}>
              {HEADERS.map((h, i) => (
                <TableCell key={h} align={RIGHT.has(i) ? "right" : i >= 9 ? "center" : "left"} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={HEADERS.length} align="center" sx={{ color: "text.secondary", py: 3 }}>
                  No activity in {year}.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => {
                const key = `${r.year}-${r.month}`;
                const isSelected = selected?.year === r.year && selected?.month === r.month;
                return (
                  <TableRow key={key} hover selected={isSelected} onClick={() => onOpenMonth(r.year, r.month)} sx={{ cursor: "pointer" }}>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{r.label}</TableCell>
                    <TableCell align="right">{fmt(r.carryForward)}</TableCell>
                    <TableCell align="right" sx={{ color: COLOR.greenDark }}>
                      {fmt(r.incomeTotal)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: COLOR.redDark }}>
                      {fmt(r.expenseTotal)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: balanceColor(r.closingBalance) }}>
                      {fmt(r.closingBalance)}
                    </TableCell>
                    <TableCell align="right">{r.actualBalance === null ? "—" : fmt(r.actualBalance)}</TableCell>
                    <TableCell align="right" sx={{ color: r.difference ? COLOR.redDark : undefined }}>
                      {r.difference === null ? "—" : fmt(r.difference)}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={RECONCILIATION_LABEL[r.reconciliationStatus]} color={REC_COLOR[r.reconciliationStatus]} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" label={MONTH_STATUS_LABEL[r.monthStatus]} color={STATUS_COLOR[r.monthStatus]} />
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={`Download ${r.label} PDF`}>
                        <span>
                          <IconButton size="small" color="error" aria-label={`Download ${r.label} PDF`} disabled={busyKey === key} onClick={() => onPdf(r.year, r.month)}>
                            <PictureAsPdfRoundedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title={`Print ${r.label}`}>
                        <span>
                          <IconButton size="small" aria-label={`Print ${r.label}`} disabled={busyKey === key} onClick={() => onPrint(r.year, r.month)}>
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default MonthlyHistory;
